/**
 * net/protocol.js — the wire format, and every rule about not trusting it.
 *
 * Pure functions only: no THREE, no PeerJS, no module state. That is what
 * makes the whole protocol testable from `__cc.net.protocolTest()` without a
 * broker, a second browser, or a network at all.
 *
 * Wire shape is versioned JSON: `{ v: 1, t: '<type>', ...fields }`. Field names
 * are short because this goes out at 10 Hz per peer; the unpacked form uses
 * readable names because everything downstream reads that instead.
 *
 * The security posture is simple and absolute: **a remote peer is an attacker.**
 * Anything arriving from the wire is clamped, sanitised, or dropped before a
 * single line of game code sees it. Positions are clamped to the hub box, names
 * are stripped to printable ASCII and length-capped, cosmetic ids must match a
 * conservative pattern (and the catalog, when one exists), and reactions must be
 * one of three literals. Nothing here can move a body, spend a ticket, or write
 * a save.
 *
 * @module net/protocol
 */

import { NET, STALL_IDS, VISIT_REACTIONS } from '../constants.js';

/** The nine message types. Anything else is dropped on arrival. */
export const MSG = Object.freeze({
  HELLO: 'hello',
  POSE: 'pose',
  COSMETICS: 'cosmetics',
  THROW: 'throw',
  STALL: 'stall',
  VISIT_REQ: 'visit-req',
  VISIT_DATA: 'visit-data',
  REACTION: 'reaction',
  BYE: 'bye',
});

/** @type {string[]} Every valid type, in protocol order. */
export const MSG_TYPES = Object.freeze(Object.values(MSG));

/** Cosmetic ids are `slot-ish` kebab identifiers and nothing else. */
const COSMETIC_ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;

/**
 * Characters a display name may contain. An allowlist, not a denylist: the
 * question "what is a name?" has a short answer, whereas "what is an attack?"
 * does not.
 */
const NAME_ALLOWED_RE = /[^A-Za-z0-9 '.!-]/g;

/**
 * Markup-shaped characters. A name containing any of these is not a name that
 * got slightly mangled — it is someone probing, so it is discarded whole rather
 * than stripped down into something like `img srcx onerroralert1` and hung over
 * a goose's head.
 */
const NAME_HOSTILE_RE = /[<>&"`\\/=]/;

/** Avatar modes the hub knows how to render. */
const MODES = Object.freeze(['third', 'first']);

// ─────────────────────────────────────────────────────────────────────────────
// Sanitisers — every one of these is also the answer to "what if a peer lies?"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reduce an arbitrary value to a safe, printable display name.
 *
 * Names end up in two places — a canvas fillText on a floating tag, and the
 * presence badge HTML — so neither markup nor control characters may survive.
 * Anything markup-shaped is replaced outright with a goose pun rather than
 * salvaged: a tag reading `img srcx onerroralert1` is not a win over one
 * reading `Gandergalf`. Whatever is left is filtered to the allowlist,
 * whitespace-collapsed, and length-capped.
 *
 * @param {unknown} raw
 * @param {string} [fallback]
 * @returns {string}
 */
export function sanitizeName(raw, fallback = 'Anonymous Goose') {
  if (typeof raw !== 'string') return fallback;
  if (NAME_HOSTILE_RE.test(raw)) return fallback;

  const cleaned = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .replace(NAME_ALLOWED_RE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NET.maxNameLength);
  return cleaned.length > 0 ? cleaned : fallback;
}

/**
 * Coerce a value to a finite number, or a default.
 * @param {unknown} v
 * @param {number} [fallback]
 * @returns {number}
 */
export function num(v, fallback = 0) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** @param {number} v @param {number} lo @param {number} hi @returns {number} */
export function clamp(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Clamp an arbitrary triple into the hub's bounding box.
 *
 * A peer that sends `[1e9, NaN, -Infinity]` gets a goose standing at the edge
 * of the midway rather than a renderer full of NaN matrices — which is exactly
 * the failure mode this exists to prevent.
 *
 * @param {unknown} raw
 * @returns {[number, number, number]}
 */
export function clampPosition(raw) {
  const a = Array.isArray(raw) ? raw : [];
  const b = NET.bounds;
  return [
    clamp(num(a[0]), -b.x, b.x),
    clamp(num(a[1]), b.yMin, b.yMax),
    clamp(num(a[2]), -b.z, b.z),
  ];
}

/**
 * Clamp a velocity triple to a sane throw envelope.
 * @param {unknown} raw
 * @param {number} [max] m/s per axis.
 * @returns {[number, number, number]}
 */
export function clampVelocity(raw, max = 40) {
  const a = Array.isArray(raw) ? raw : [];
  return [
    clamp(num(a[0]), -max, max),
    clamp(num(a[1]), -max, max),
    clamp(num(a[2]), -max, max),
  ];
}

/** Wrap any number into (−π, π]. @param {unknown} raw @returns {number} */
export function wrapAngle(raw) {
  let a = num(raw);
  if (!Number.isFinite(a)) return 0;
  a = a % (Math.PI * 2);
  if (a > Math.PI) a -= Math.PI * 2;
  if (a <= -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * @param {unknown} id
 * @returns {string|null} The id if it is one of the twelve GDD stalls.
 */
export function sanitizeStallId(id) {
  return typeof id === 'string' && STALL_IDS.includes(id) ? id : null;
}

/** @param {unknown} m @returns {'third'|'first'} */
export function sanitizeMode(m) {
  return MODES.includes(m) ? m : 'third';
}

/** @param {unknown} r @returns {string|null} One of honk/applause/wow. */
export function sanitizeReaction(r) {
  return typeof r === 'string' && VISIT_REACTIONS.includes(r) ? r : null;
}

/**
 * Filter a cosmetics record down to ids that are safe to look up.
 *
 * `allowed` is the live catalog from the meta agent. While that catalog is
 * empty — which it is until the meta agent ships one — pattern validation is
 * the only gate, and an unknown id renders as a greybox stand-in rather than
 * nothing. Once a catalog exists, ids outside it are dropped.
 *
 * @param {unknown} raw
 * @param {Set<string>|null} [allowed]
 * @returns {Record<string, string>}
 */
export function sanitizeCosmetics(raw, allowed = null) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const slot of ['head', 'body', 'beak', 'feet', 'aura']) {
    const id = raw[slot];
    if (typeof id !== 'string' || !COSMETIC_ID_RE.test(id)) continue;
    if (allowed && allowed.size > 0 && !allowed.has(id)) continue;
    out[slot] = id;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Packing — one helper per type, each returning a plain JSON-safe object
// ─────────────────────────────────────────────────────────────────────────────

/** @param {string} type @returns {{v:number,t:string}} */
function envelope(type) {
  return { v: NET.protocolVersion, t: type };
}

/**
 * @param {{ name:string, cosmetics?:object, position?:number[], yaw?:number,
 *           mode?:string, activeStall?:string|null }} p
 */
export function packHello(p) {
  return {
    ...envelope(MSG.HELLO),
    n: p.name,
    c: p.cosmetics || {},
    p: p.position || [0, 0, 0],
    y: p.yaw || 0,
    m: p.mode || 'third',
    s: p.activeStall || null,
  };
}

/**
 * @param {{ position:number[], yaw:number, mode?:string,
 *           activeStall?:string|null }} p
 */
export function packPose(p) {
  return {
    ...envelope(MSG.POSE),
    p: p.position,
    y: p.yaw,
    m: p.mode || 'third',
    s: p.activeStall || null,
  };
}

/** @param {{ cosmetics:object }} p */
export function packCosmetics(p) {
  return { ...envelope(MSG.COSMETICS), c: p.cosmetics || {} };
}

/**
 * A throw, as a closed-form arc the receiver replays cosmetically (GDD 10.4).
 * @param {{ stallId:string, origin:number[], velocity:number[],
 *           gravityScale?:number, kind?:string }} p
 */
export function packThrow(p) {
  return {
    ...envelope(MSG.THROW),
    s: p.stallId,
    o: p.origin,
    vel: p.velocity,
    g: p.gravityScale ?? 1,
    k: p.kind || 'baseball',
  };
}

/** @param {{ stallId:string|null }} p */
export function packStall(p) {
  return { ...envelope(MSG.STALL), s: p.stallId || null };
}

/** @returns {object} */
export function packVisitReq() {
  return envelope(MSG.VISIT_REQ);
}

/** @param {{ data:object }} p */
export function packVisitData(p) {
  return { ...envelope(MSG.VISIT_DATA), d: p.data || {} };
}

/** @param {{ reaction:string, fromName:string }} p */
export function packReaction(p) {
  return { ...envelope(MSG.REACTION), r: p.reaction, n: p.fromName };
}

/** @param {{ reason?:string }} [p] */
export function packBye(p = {}) {
  return { ...envelope(MSG.BYE), why: p.reason || 'left' };
}

/** Every packer, keyed by type — used by the round-trip self-test. */
export const PACKERS = Object.freeze({
  [MSG.HELLO]: packHello,
  [MSG.POSE]: packPose,
  [MSG.COSMETICS]: packCosmetics,
  [MSG.THROW]: packThrow,
  [MSG.STALL]: packStall,
  [MSG.VISIT_REQ]: packVisitReq,
  [MSG.VISIT_DATA]: packVisitData,
  [MSG.REACTION]: packReaction,
  [MSG.BYE]: packBye,
});

// ─────────────────────────────────────────────────────────────────────────────
// Unpacking — the only door remote data comes through
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validate and normalise a raw wire message.
 *
 * @param {unknown} raw
 * @param {{ allowedCosmetics?: Set<string>|null }} [opts]
 * @returns {object|null} A normalised message with a `type` field, or null if
 *   the version is wrong, the type is unknown, or the payload is unusable.
 */
export function unpack(raw, opts = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (raw.v !== NET.protocolVersion) return null;
  if (typeof raw.t !== 'string' || !MSG_TYPES.includes(raw.t)) return null;
  const allowed = opts.allowedCosmetics ?? null;

  switch (raw.t) {
    case MSG.HELLO:
      return {
        type: MSG.HELLO,
        name: sanitizeName(raw.n),
        cosmetics: sanitizeCosmetics(raw.c, allowed),
        position: clampPosition(raw.p),
        yaw: wrapAngle(raw.y),
        mode: sanitizeMode(raw.m),
        activeStall: sanitizeStallId(raw.s),
      };

    case MSG.POSE:
      return {
        type: MSG.POSE,
        position: clampPosition(raw.p),
        yaw: wrapAngle(raw.y),
        mode: sanitizeMode(raw.m),
        activeStall: sanitizeStallId(raw.s),
      };

    case MSG.COSMETICS:
      return { type: MSG.COSMETICS, cosmetics: sanitizeCosmetics(raw.c, allowed) };

    case MSG.THROW: {
      const stallId = sanitizeStallId(raw.s);
      // A throw with no valid stall has nowhere to be rendered, so it is noise.
      if (!stallId) return null;
      return {
        type: MSG.THROW,
        stallId,
        origin: clampPosition(raw.o),
        velocity: clampVelocity(raw.vel),
        gravityScale: clamp(num(raw.g, 1), 0.1, 3),
        kind: sanitizeName(raw.k, 'baseball').toLowerCase().replace(/[^a-z-]/g, '') || 'baseball',
      };
    }

    case MSG.STALL:
      return { type: MSG.STALL, stallId: sanitizeStallId(raw.s) };

    case MSG.VISIT_REQ:
      return { type: MSG.VISIT_REQ };

    case MSG.VISIT_DATA:
      return { type: MSG.VISIT_DATA, data: sanitizeVisitData(raw.d) };

    case MSG.REACTION: {
      const reaction = sanitizeReaction(raw.r);
      if (!reaction) return null;
      return { type: MSG.REACTION, reaction, fromName: sanitizeName(raw.n) };
    }

    case MSG.BYE:
      return { type: MSG.BYE, reason: sanitizeName(raw.why, 'left') };

    default:
      return null;
  }
}

/**
 * A visit payload is another player's whole space, so it is the largest thing
 * on the wire and the one worth size-capping. Contents are handed to the space
 * module, which renders a read-only copy — it is never merged into a save.
 *
 * @param {unknown} raw
 * @returns {object}
 */
function sanitizeVisitData(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  let json;
  try {
    json = JSON.stringify(raw);
  } catch {
    return {};
  }
  if (json.length > NET.maxVisitBytes) return { truncated: true };
  const out = { ...raw };
  if (out.ownerName !== undefined) out.ownerName = sanitizeName(out.ownerName);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inbound rate limiting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A sliding one-second window per peer. Over the cap, messages are dropped —
 * not queued — because the only thing a flood can cost us is frame time, and
 * dropping a pose is free (the next one is 100 ms away).
 */
export class RateLimiter {
  /** @param {number} [perSecond] */
  constructor(perSecond = NET.maxInboundPerSecond) {
    this.perSecond = perSecond;
    /** @type {Map<string, number[]>} */
    this.windows = new Map();
    this.dropped = 0;
  }

  /**
   * @param {string} peerId
   * @param {number} [now] ms timestamp; injectable so tests need no clock.
   * @returns {boolean} True if this message is within budget.
   */
  allow(peerId, now = Date.now()) {
    let w = this.windows.get(peerId);
    if (!w) {
      w = [];
      this.windows.set(peerId, w);
    }
    const cutoff = now - 1000;
    while (w.length > 0 && w[0] < cutoff) w.shift();
    if (w.length >= this.perSecond) {
      this.dropped++;
      return false;
    }
    w.push(now);
    return true;
  }

  /** @param {string} peerId */
  forget(peerId) {
    this.windows.delete(peerId);
  }

  reset() {
    this.windows.clear();
    this.dropped = 0;
  }
}

/**
 * Pack then unpack a message, for the `__cc.net.protocolTest()` hook.
 *
 * Returns both halves plus a verdict so a Playwright test can assert the round
 * trip, the version gate and the sanitisers in one call without importing
 * anything from /src (see ARCHITECTURE §11).
 *
 * @param {{ type:string, payload?:object, corrupt?:object }} spec
 * @returns {{ ok:boolean, type:string, packed:object|null, unpacked:object|null }}
 */
export function roundTrip(spec = {}) {
  const type = spec.type;
  const packer = PACKERS[type];
  if (!packer) return { ok: false, type, packed: null, unpacked: null };

  const packed = { ...packer(spec.payload || {}), ...(spec.corrupt || {}) };
  const unpacked = unpack(packed);
  return {
    ok: unpacked !== null && unpacked.type === type,
    type,
    packed,
    unpacked,
  };
}
